import React, { useContext, useEffect, useReducer, useState, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { Store } from '../../Store';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { getError } from '../../utils';
import { api } from '../../Api';

/* ──────────────────────────────────────────────────────────────────────────
   Mega User Report — a single dossier page that pulls every collection in
   the system tied to one user (orders, withdraws, transfers, conversions,
   ledger entries, network, promotions, notifications, sessions, chats)
   and renders it as an infographic + tabbed history with tree views.
   ────────────────────────────────────────────────────────────────────── */

const reducer = (state, action) => {
  switch (action.type) {
    case 'FETCH_REQUEST':
      return { ...state, loading: true, error: '' };
    case 'FETCH_SUCCESS':
      return { ...state, report: action.payload, loading: false };
    case 'FETCH_FAIL':
      return { ...state, loading: false, error: action.payload };
    default:
      return state;
  }
};

// ── helpers ───────────────────────────────────────────────────────────────
const fmtNum = (n) =>
  n === undefined || n === null
    ? '0'
    : Number(n).toLocaleString('en-US', { maximumFractionDigits: 2 });

const fmtMoney = (n) => '৳ ' + fmtNum(n);

const fmtDate = (d) => {
  if (!d) return '—';
  const date = new Date(d);
  if (isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};
const fmtDateTime = (d) => {
  if (!d) return '—';
  const date = new Date(d);
  if (isNaN(date.getTime())) return '—';
  return (
    date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) +
    ' · ' +
    date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
  );
};

const initials = (s) => (s || '?').toString().trim().slice(0, 2).toUpperCase();

// YYYY-MM-DD in local time (avoids UTC date-shift bug from toISOString().slice(0,10))
const toLocalISODate = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const todayISO = () => toLocalISODate(new Date());

const daysAgoISO = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return toLocalISODate(d);
};

const startOfMonthISO = (offset = 0) => {
  const d = new Date();
  d.setMonth(d.getMonth() + offset, 1);
  return toLocalISODate(d);
};

const endOfMonthISO = (offset = 0) => {
  const d = new Date();
  d.setMonth(d.getMonth() + offset + 1, 0);
  return toLocalISODate(d);
};

export default function UserReportScreen() {
  const { state } = useContext(Store);
  const { userInfo } = state;
  const navigate = useNavigate();
  const { id: userId } = useParams();

  const [{ loading, error, report }, dispatch] = useReducer(reducer, {
    loading: true,
    error: '',
    report: null,
  });
  const [tab, setTab] = useState('timeline');

  // Date range — default to last 7 days. Updating these refetches.
  const [startDate, setStartDate] = useState(daysAgoISO(6));
  const [endDate, setEndDate] = useState(todayISO());
  const [activePreset, setActivePreset] = useState('7d');

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      dispatch({ type: 'FETCH_REQUEST' });
      try {
        const { data } = await axios.get(
          api +
            `/api/users/admin/megareport/${userId}?startDate=${startDate}&endDate=${endDate}`,
          { headers: { Authorization: `Bearer ${userInfo.token}` } }
        );
        if (!cancelled) dispatch({ type: 'FETCH_SUCCESS', payload: data });
      } catch (err) {
        if (!cancelled) dispatch({ type: 'FETCH_FAIL', payload: getError(err) });
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [userId, userInfo.token, startDate, endDate]);

  // Preset handler
  const applyPreset = (key) => {
    setActivePreset(key);
    const today = todayISO();
    switch (key) {
      case 'today':
        setStartDate(today);
        setEndDate(today);
        break;
      case 'yesterday': {
        const y = daysAgoISO(1);
        setStartDate(y);
        setEndDate(y);
        break;
      }
      case '7d':
        setStartDate(daysAgoISO(6));
        setEndDate(today);
        break;
      case '30d':
        setStartDate(daysAgoISO(29));
        setEndDate(today);
        break;
      case 'thisMonth':
        setStartDate(startOfMonthISO(0));
        setEndDate(today);
        break;
      case 'lastMonth':
        setStartDate(startOfMonthISO(-1));
        setEndDate(endOfMonthISO(-1));
        break;
      case 'allTime':
        setStartDate('2020-01-01');
        setEndDate(today);
        break;
      default:
        break;
    }
  };

  // Initial load — no report yet
  if (loading && !report) {
    return (
      <div style={S.shell}>
        <div style={S.centerBox}>
          <div style={S.spinner} />
          <p style={{ marginTop: 16, color: '#64748b' }}>Building user report…</p>
        </div>
        <SpinnerCSS />
      </div>
    );
  }

  if (error && !report) {
    return (
      <div style={S.shell}>
        <div style={S.errorBox}>
          <i className="fa fa-exclamation-triangle" style={{ fontSize: 36, color: '#dc2626' }} />
          <h4 style={{ marginTop: 12, color: '#991b1b' }}>Could not load report</h4>
          <p style={{ color: '#7f1d1d' }}>{error}</p>
          <button onClick={() => navigate(-1)} style={S.btnGhost}>
            <i className="fa fa-arrow-left" /> Go back
          </button>
        </div>
      </div>
    );
  }

  if (!report) return null;

  const { user, summary, upline, downline, history, tree, range, meta } = report;

  return (
    <div style={S.shell}>
      <Helmet>
        <title>{user.name} · Full User Report</title>
      </Helmet>

      {/* ── HERO PROFILE BANNER ─────────────────────────────────────── */}
      <div style={S.hero}>
        <div style={S.heroPattern} />
        <button onClick={() => navigate(-1)} style={S.backBtn}>
          <i className="fa fa-arrow-left" /> Back to Users
        </button>

        <div style={S.heroBody}>
          <div style={S.heroAvatar}>
            {user.profilePhoto &&
            !user.profilePhoto.includes('clickcart-logo') ? (
              <img
                src={user.profilePhoto}
                alt=""
                style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }}
              />
            ) : (
              <span>{initials(user.name)}</span>
            )}
          </div>
          <div style={S.heroInfo}>
            <h1 style={S.heroName}>{user.name}</h1>
            <div style={S.heroMeta}>
              <span style={S.heroChip}><i className="fa fa-at" /> {user.username}</span>
              {user.phone && <span style={S.heroChip}><i className="fa fa-phone" /> {user.phone}</span>}
              {user.email && <span style={S.heroChip}><i className="fa fa-envelope" /> {user.email}</span>}
              <span style={S.heroChip}><i className="fa fa-calendar" /> Joined {fmtDate(user.createdAt)}</span>
            </div>
            <div style={S.heroBadges}>
              {user.status ? (
                <Badge color="#16a34a" icon="fa-check-circle">Active</Badge>
              ) : (
                <Badge color="#dc2626" icon="fa-ban">Disabled</Badge>
              )}
              {user.isBlocked && <Badge color="#dc2626" icon="fa-lock">Blocked</Badge>}
              {user.isAdmin && <Badge color="#7c3aed" icon="fa-shield">Admin</Badge>}
              {user.isAgent && <Badge color="#2563eb" icon="fa-user-secret">Agent</Badge>}
              {user.isActiveId && <Badge color="#0891b2" icon="fa-id-card">Active ID</Badge>}
              <Badge color="#f59e0b" icon="fa-trophy">
                Designation #{user.designation || 0}
              </Badge>
            </div>
          </div>
          <div style={S.heroActions}>
            <Link to={`/admin/edituser/${user._id}`} style={S.btnLight}>
              <i className="fa fa-pencil" /> Edit
            </Link>
            <button
              onClick={() => window.print()}
              style={S.btnLight}
            >
              <i className="fa fa-print" /> Print
            </button>
          </div>
        </div>
      </div>

      {/* ── DATE-RANGE FILTER BAR ───────────────────────────────────── */}
      <DateFilterBar
        loading={loading}
        startDate={startDate}
        endDate={endDate}
        activePreset={activePreset}
        onPreset={applyPreset}
        onStartChange={(v) => {
          setActivePreset('custom');
          setStartDate(v);
        }}
        onEndChange={(v) => {
          setActivePreset('custom');
          setEndDate(v);
        }}
        rangeLabel={`${range.startLabel} → ${range.endLabel}`}
        eventsInRange={range.totalEvents}
      />

      {/* ── RANGE KPI STRIP — totals just for the filtered window ───── */}
      <RangeKpiStrip range={range} />

      {/* ── INFOGRAPHIC SUMMARY CARDS (lifetime) ────────────────────── */}
      <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.5px', margin: '4px 4px 10px' }}>
        Lifetime Snapshot
      </div>
      <div style={S.summaryGrid}>
        <SummaryCard
          icon="fa-coins"
          label="Points"
          value={fmtNum(summary.points)}
          gradient="linear-gradient(135deg,#f59e0b 0%,#d97706 100%)"
          subtitle="Current balance"
        />
        <SummaryCard
          icon="fa-wallet"
          label="Available Balance"
          value={fmtMoney(summary.availableBalance)}
          gradient="linear-gradient(135deg,#10b981 0%,#059669 100%)"
          subtitle="Withdrawable"
        />
        <SummaryCard
          icon="fa-shopping-bag"
          label="Shopping Balance"
          value={fmtMoney(summary.shoppingBalance)}
          gradient="linear-gradient(135deg,#8b5cf6 0%,#6d28d9 100%)"
          subtitle="Store credit"
        />
        <SummaryCard
          icon="fa-money-bill-wave"
          label="Total Withdrawn"
          value={fmtMoney(summary.totalWithdrawn)}
          gradient="linear-gradient(135deg,#0ea5e9 0%,#0369a1 100%)"
          subtitle={`${summary.counts.withdrawals} requests · ${fmtMoney(summary.totalWithdrawPending)} pending`}
        />
        <SummaryCard
          icon="fa-arrow-up"
          label="Points Sent"
          value={fmtNum(summary.totalPointsSent)}
          gradient="linear-gradient(135deg,#ef4444 0%,#b91c1c 100%)"
          subtitle={`${summary.counts.pointsSent} transfers`}
        />
        <SummaryCard
          icon="fa-arrow-down"
          label="Points Received"
          value={fmtNum(summary.totalPointsReceived)}
          gradient="linear-gradient(135deg,#22c55e 0%,#15803d 100%)"
          subtitle={`${summary.counts.pointsReceived} received`}
        />
        <SummaryCard
          icon="fa-shopping-cart"
          label="Total Spent (Orders)"
          value={fmtMoney(summary.totalOrdersValue)}
          gradient="linear-gradient(135deg,#f43f5e 0%,#be123c 100%)"
          subtitle={`${summary.counts.orders} orders · ${summary.ordersDelivered} delivered`}
        />
        <SummaryCard
          icon="fa-users"
          label="Direct Referrals"
          value={fmtNum(summary.directReferrals)}
          gradient="linear-gradient(135deg,#06b6d4 0%,#0e7490 100%)"
          subtitle={`Tree: L:${summary.leftCount} · R:${summary.rightCount}`}
        />
      </div>

      {/* ── EARNINGS BREAKDOWN BAR ──────────────────────────────────── */}
      <EarningsBreakdown summary={summary} />

      {/* ── TAB BAR ─────────────────────────────────────────────────── */}
      <div style={S.tabBar}>
        {[
          { key: 'timeline', icon: 'fa-calendar', label: 'Daily Timeline', count: range.totalEvents },
          { key: 'overview', icon: 'fa-th-large', label: 'Overview' },
          { key: 'profile', icon: 'fa-user', label: 'Profile' },
          { key: 'financial', icon: 'fa-money-bill', label: 'Financial', count: range.totals.withdrawals.count + range.totals.conversions.count },
          { key: 'transfers', icon: 'fa-exchange', label: 'Transfers', count: range.totals.pointsSent.count + range.totals.pointsReceived.count },
          { key: 'earnings', icon: 'fa-trophy', label: 'Earnings Ledger', count: range.totals.availableBalance.count + range.totals.matchingPoints.count + range.totals.referencePoints.count + range.totals.shoppingPoints.count + range.totals.incomeGeneration.count },
          { key: 'orders', icon: 'fa-shopping-cart', label: 'Orders', count: range.totals.orders.count },
          { key: 'network', icon: 'fa-sitemap', label: 'Network Tree' },
          { key: 'promotions', icon: 'fa-trophy', label: 'Promotions', count: range.totals.promotions.count + range.totals.globalHistory.count },
          { key: 'activity', icon: 'fa-bell', label: 'Activity', count: range.totals.notifications.count + range.totals.chats.count + (history.sessions?.length || 0) },
        ].map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            style={{
              ...S.tabBtn,
              ...(tab === t.key ? S.tabBtnActive : {}),
            }}
          >
            <i className={`fa ${t.icon}`} /> {t.label}
            {t.count > 0 && (
              <span style={{ ...S.tabCount, ...(tab === t.key ? S.tabCountActive : {}) }}>
                {t.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* ── TAB CONTENT ─────────────────────────────────────────────── */}
      <div style={S.tabPanel}>
        {tab === 'timeline' && (
          <DailyTimelineTab history={history} range={range} />
        )}
        {tab === 'overview' && (
          <OverviewTab user={user} summary={summary} upline={upline} downline={downline} history={history} />
        )}
        {tab === 'profile' && <ProfileTab user={user} tree={tree} />}
        {tab === 'financial' && <FinancialTab history={history} />}
        {tab === 'transfers' && <TransfersTab history={history} username={user.username} />}
        {tab === 'earnings' && <EarningsTab history={history} />}
        {tab === 'orders' && <OrdersTab history={history} />}
        {tab === 'network' && <NetworkTab user={user} upline={upline} downline={downline} />}
        {tab === 'promotions' && <PromotionsTab history={history} />}
        {tab === 'activity' && <ActivityTab history={history} />}
      </div>

      <div style={S.footer}>
        Range: <strong>{range.startLabel}</strong> → <strong>{range.endLabel}</strong> · {range.totalEvents} events ·
        report generated {fmtDateTime(meta.generatedAt)} · up to {meta.recordCap} records per collection
      </div>

      <ResponsiveCSS />
    </div>
  );
}

/* ───────────────────────── date filter bar ───────────────────────── */

const PRESETS = [
  { key: 'today', label: 'Today', icon: 'fa-sun-o' },
  { key: 'yesterday', label: 'Yesterday', icon: 'fa-moon-o' },
  { key: '7d', label: 'Last 7 Days', icon: 'fa-calendar-o' },
  { key: '30d', label: 'Last 30 Days', icon: 'fa-calendar' },
  { key: 'thisMonth', label: 'This Month', icon: 'fa-calendar-check-o' },
  { key: 'lastMonth', label: 'Last Month', icon: 'fa-calendar-minus-o' },
  { key: 'allTime', label: 'All Time', icon: 'fa-infinity' },
];

function DateFilterBar({
  loading,
  startDate,
  endDate,
  activePreset,
  onPreset,
  onStartChange,
  onEndChange,
  rangeLabel,
  eventsInRange,
}) {
  return (
    <div style={S.filterBar}>
      <div style={S.filterHeader}>
        <div>
          <div style={S.filterTitle}>
            <i className="fa fa-filter" style={{ marginRight: 8, color: '#6366f1' }} />
            Date Filter
          </div>
          <div style={S.filterSub}>
            Showing <strong>{eventsInRange}</strong> events between{' '}
            <strong>{rangeLabel}</strong>
            {loading && (
              <span style={{ marginLeft: 10, color: '#6366f1' }}>
                <i className="fa fa-spinner fa-spin" /> filtering…
              </span>
            )}
          </div>
        </div>
      </div>

      <div style={S.presetRow}>
        {PRESETS.map((p) => (
          <button
            key={p.key}
            onClick={() => onPreset(p.key)}
            style={{
              ...S.presetBtn,
              ...(activePreset === p.key ? S.presetBtnActive : {}),
            }}
          >
            <i className={`fa ${p.icon}`} /> {p.label}
          </button>
        ))}
      </div>

      <div style={S.customRange}>
        <div style={S.dateGroup}>
          <label style={S.dateLabel}>From</label>
          <input
            type="date"
            value={startDate}
            max={endDate}
            onChange={(e) => onStartChange(e.target.value)}
            style={S.dateInput}
          />
        </div>
        <div style={{ color: '#94a3b8', alignSelf: 'flex-end', paddingBottom: 10 }}>
          <i className="fa fa-arrow-right" />
        </div>
        <div style={S.dateGroup}>
          <label style={S.dateLabel}>To</label>
          <input
            type="date"
            value={endDate}
            min={startDate}
            max={todayISO()}
            onChange={(e) => onEndChange(e.target.value)}
            style={S.dateInput}
          />
        </div>
        <span
          style={{
            ...S.presetBtn,
            ...(activePreset === 'custom' ? S.presetBtnActive : {}),
            cursor: 'default',
            alignSelf: 'flex-end',
          }}
        >
          <i className="fa fa-pencil" /> Custom
        </span>
      </div>
    </div>
  );
}

/* ───────────────────────── range KPI strip ───────────────────────── */

function RangeKpiStrip({ range }) {
  const items = [
    { icon: 'fa-money-bill-wave', label: 'Withdrawals', count: range.totals.withdrawals.count, total: fmtMoney(range.totals.withdrawals.total), color: '#0ea5e9' },
    { icon: 'fa-exchange', label: 'Conversions', count: range.totals.conversions.count, total: fmtNum(range.totals.conversions.total), color: '#8b5cf6' },
    { icon: 'fa-arrow-up', label: 'Sent', count: range.totals.pointsSent.count, total: fmtNum(range.totals.pointsSent.total), color: '#ef4444' },
    { icon: 'fa-arrow-down', label: 'Received', count: range.totals.pointsReceived.count, total: fmtNum(range.totals.pointsReceived.total), color: '#22c55e' },
    { icon: 'fa-shopping-cart', label: 'Orders', count: range.totals.orders.count, total: fmtMoney(range.totals.orders.total), color: '#f43f5e' },
    { icon: 'fa-wallet', label: 'Bal. Earned', count: range.totals.availableBalance.count, total: fmtMoney(range.totals.availableBalance.total), color: '#10b981' },
    { icon: 'fa-handshake-o', label: 'Matching', count: range.totals.matchingPoints.count, total: fmtNum(range.totals.matchingPoints.total), color: '#6366f1' },
    { icon: 'fa-link', label: 'Reference', count: range.totals.referencePoints.count, total: fmtNum(range.totals.referencePoints.total), color: '#f59e0b' },
  ];
  return (
    <div style={S.kpiStrip}>
      {items.map((it, i) => (
        <div key={i} style={S.kpiCell}>
          <div style={{ ...S.kpiIcon, background: it.color }}>
            <i className={`fa ${it.icon}`} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={S.kpiTotal}>{it.total}</div>
            <div style={S.kpiLabel}>
              {it.label} · {it.count}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ───────────────────────── collapsible card ───────────────────────── */

function Collapsible({ icon, title, count, summaryRight, defaultOpen = true, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={S.card}>
      <button onClick={() => setOpen(!open)} style={S.collapseHead}>
        <span style={S.collapseHeadL}>
          <i
            className={`fa fa-chevron-${open ? 'down' : 'right'}`}
            style={{ color: '#6366f1', width: 14, fontSize: 12 }}
          />
          <i className={`fa ${icon}`} style={{ color: '#6366f1', marginLeft: 4 }} />
          <span style={{ fontSize: 16, fontWeight: 700, color: '#0f172a' }}>{title}</span>
          {count !== undefined && (
            <span style={S.sectionCount}>{count}</span>
          )}
        </span>
        {summaryRight && (
          <span style={{ fontSize: 12, color: '#64748b', fontWeight: 600 }}>{summaryRight}</span>
        )}
      </button>
      {open && <div style={{ marginTop: 14 }}>{children}</div>}
    </div>
  );
}

/* ───────────────────────── small reusable bits ───────────────────────── */

function Badge({ color, icon, children }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '5px 12px',
        borderRadius: 99,
        fontSize: 12,
        fontWeight: 600,
        background: 'rgba(255,255,255,0.95)',
        color,
        border: `1.5px solid ${color}`,
      }}
    >
      <i className={`fa ${icon}`} /> {children}
    </span>
  );
}

function SummaryCard({ icon, label, value, subtitle, gradient }) {
  return (
    <div style={{ ...S.sumCard, background: gradient }}>
      <div style={S.sumIcon}>
        <i className={`fa ${icon}`} />
      </div>
      <div style={S.sumValue}>{value}</div>
      <div style={S.sumLabel}>{label}</div>
      {subtitle && <div style={S.sumSub}>{subtitle}</div>}
    </div>
  );
}

function StatCard({ label, value, color = '#0f172a', icon }) {
  return (
    <div style={S.statCard}>
      <div style={{ ...S.statValue, color }}>
        {icon && <i className={`fa ${icon}`} style={{ marginRight: 8 }} />}
        {value}
      </div>
      <div style={S.statLabel}>{label}</div>
    </div>
  );
}

function SectionTitle({ icon, children, count }) {
  return (
    <h3 style={S.sectionTitle}>
      <i className={`fa ${icon}`} style={{ marginRight: 10, color: '#6366f1' }} />
      {children}
      {count !== undefined && <span style={S.sectionCount}>{count}</span>}
    </h3>
  );
}

function EmptyState({ icon = 'fa-inbox', message }) {
  return (
    <div style={S.empty}>
      <i className={`fa ${icon}`} style={{ fontSize: 38, color: '#cbd5e1' }} />
      <p style={{ margin: '12px 0 0', color: '#94a3b8', fontSize: 14 }}>{message}</p>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    Approved: '#16a34a',
    approved: '#16a34a',
    success: '#16a34a',
    Paid: '#16a34a',
    Pending: '#f59e0b',
    pending: '#f59e0b',
    Rejected: '#dc2626',
    failed: '#dc2626',
    cancelled: '#6b7280',
    true: '#16a34a',
    false: '#f59e0b',
  };
  const text = String(status);
  const color = map[text] || '#6366f1';
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '3px 10px',
        borderRadius: 99,
        fontSize: 11,
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '.4px',
        color: '#fff',
        background: color,
      }}
    >
      {text}
    </span>
  );
}

function EarningsBreakdown({ summary }) {
  const items = [
    { label: 'Available Balance Earnings', value: summary.totalEarnedAvailable, color: '#10b981' },
    { label: 'Matching Points', value: summary.totalEarnedMatching, color: '#6366f1' },
    { label: 'Reference Points', value: summary.totalEarnedReference, color: '#f59e0b' },
    { label: 'Shopping Points', value: summary.totalEarnedShopping, color: '#8b5cf6' },
    { label: 'Income Generation', value: summary.totalIncomeGeneration, color: '#06b6d4' },
  ];
  const total = items.reduce((a, b) => a + (b.value || 0), 0);
  if (total <= 0) return null;
  return (
    <div style={S.card}>
      <SectionTitle icon="fa-chart-pie">Lifetime Earnings Breakdown — {fmtMoney(total)}</SectionTitle>
      <div style={S.barChart}>
        {items.map((it, i) => {
          const pct = total > 0 ? (it.value / total) * 100 : 0;
          if (pct <= 0) return null;
          return (
            <div
              key={i}
              style={{ width: `${pct}%`, background: it.color }}
              title={`${it.label}: ${fmtMoney(it.value)} (${pct.toFixed(1)}%)`}
            />
          );
        })}
      </div>
      <div style={S.legendRow}>
        {items.map((it, i) => (
          <div key={i} style={S.legendItem}>
            <span style={{ ...S.legendDot, background: it.color }} />
            <div>
              <div style={{ fontSize: 12, color: '#64748b', fontWeight: 500 }}>{it.label}</div>
              <div style={{ fontSize: 14, color: '#0f172a', fontWeight: 700 }}>{fmtMoney(it.value)}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ───────────────────────── daily timeline tab ───────────────────────── */

// Maps every event in `history` to a uniform shape for the day-grouped tree.
// Each shaped event has: when (Date string), category key, title, sub, color, icon.
const CATEGORIES = {
  withdrawals: { label: 'Withdrawals', icon: 'fa-money-bill-wave', color: '#0ea5e9' },
  conversions: { label: 'Conversions', icon: 'fa-exchange', color: '#8b5cf6' },
  pointsSent: { label: 'Points Sent', icon: 'fa-arrow-up', color: '#ef4444' },
  pointsReceived: { label: 'Points Received', icon: 'fa-arrow-down', color: '#22c55e' },
  orders: { label: 'Orders', icon: 'fa-shopping-cart', color: '#f43f5e' },
  availableBalance: { label: 'Available Balance', icon: 'fa-wallet', color: '#10b981' },
  matchingPoints: { label: 'Matching Points', icon: 'fa-handshake-o', color: '#6366f1' },
  shoppingPoints: { label: 'Shopping Points', icon: 'fa-shopping-bag', color: '#a855f7' },
  referencePoints: { label: 'Reference Points', icon: 'fa-link', color: '#f59e0b' },
  incomeGenerations: { label: 'Income Generation', icon: 'fa-bolt', color: '#06b6d4' },
  notifications: { label: 'Notifications', icon: 'fa-bell', color: '#64748b' },
  promotions: { label: 'Promotions', icon: 'fa-trophy', color: '#eab308' },
  globalHistory: { label: 'Global Pool', icon: 'fa-globe', color: '#0891b2' },
  chats: { label: 'Support Chats', icon: 'fa-comments', color: '#14b8a6' },
};

function shapeEvents(history) {
  const out = [];
  const push = (cat, item, title, sub, amount) =>
    out.push({ cat, when: item.createdAt, title, sub, amount, raw: item });

  (history.withdrawals || []).forEach((w) =>
    push('withdrawals', w, `Withdrew ${fmtMoney(w.amount)}`, `via ${w.paymentmethod} → ${w.to} · ${w.status ? 'Paid' : 'Pending'}${w.paidBy ? ` · by ${w.paidBy}` : ''}`, w.amount)
  );
  (history.conversions || []).forEach((c) =>
    push('conversions', c, `${fmtNum(c.amount)} ${c.from} → ${c.to}`, `Net ${fmtNum(c.netAmount || c.amount)} · fee ${fmtNum(c.feeApplied)} · ${c.status}`, c.amount)
  );
  (history.pointsSent || []).forEach((p) =>
    push('pointsSent', p, `Sent ${fmtNum(p.amount)} pts`, `→ @${p.receiverUsername}${p.transferCharge ? ` · charge ${fmtNum(p.transferCharge)}` : ''}`, p.amount)
  );
  (history.pointsReceived || []).forEach((p) =>
    push('pointsReceived', p, `Received ${fmtNum(p.amount)} pts`, `← @${p.senderUsername || 'system'}${p.sendInfo ? ` · ${p.sendInfo}` : ''}`, p.amount)
  );
  (history.orders || []).forEach((o) =>
    push('orders', o, `Order ${fmtMoney(o.totalPrice)}`, `${o.orderItems?.length || 0} items · ${o.isPaid ? 'Paid' : 'Unpaid'} · ${o.isDelivered ? 'Delivered' : 'Pending'}`, o.totalPrice)
  );
  (history.availableBalance || []).forEach((a) =>
    push('availableBalance', a, `+ ${fmtMoney(a.amount)}`, a.details || a.status || '—', a.amount)
  );
  (history.matchingPoints || []).forEach((m) =>
    push('matchingPoints', m, `+ ${fmtNum(m.amount)} matching`, m.details || m.status || '—', m.amount)
  );
  (history.shoppingPoints || []).forEach((s) =>
    push('shoppingPoints', s, `+ ${fmtNum(s.amount)} shopping`, s.details || s.status || '—', s.amount)
  );
  (history.referencePoints || []).forEach((r) =>
    push('referencePoints', r, `+ ${fmtNum(r.amount)} reference`, r.details || r.status || '—', r.amount)
  );
  (history.incomeGenerations || []).forEach((g) =>
    push('incomeGenerations', g, `+ ${fmtMoney(g.amount)} (Gen #${g.generation})`, g.details || g.status || '—', g.amount)
  );
  (history.notifications || []).forEach((n) =>
    push('notifications', n, n.details || 'Notification', n.seen ? `Seen ${fmtDateTime(n.seenAt)}` : 'Unseen', 0)
  );
  (history.promotions || []).forEach((p) =>
    push('promotions', p, `Promotion #${p.position_serial} ${p.position_name}`, `Carry L:${fmtNum(p.carry_left)} R:${fmtNum(p.carry_right)} · ${p.status}`, 0)
  );
  (history.globalHistory || []).forEach((g) =>
    push('globalHistory', g, `Global pool ${fmtMoney(g.amount)}`, `Designation #${g.designation} · ${g.status}`, g.amount)
  );
  (history.chats || []).forEach((c) =>
    push('chats', c, c.message?.slice(0, 80) || 'Chat', c.reply ? `Reply: ${c.reply.slice(0, 60)}` : 'No reply yet', 0)
  );

  return out.filter((e) => e.when);
}

function DailyTimelineTab({ history, range }) {
  const allEvents = useMemo(() => shapeEvents(history), [history]);

  // Group: day → { events, categories: { catKey → events[] }, totalAmount }
  const days = useMemo(() => {
    const map = new Map();
    for (const ev of allEvents) {
      const d = new Date(ev.when);
      const key = toLocalISODate(d);
      if (!map.has(key)) map.set(key, { date: key, events: [], byCat: {} });
      const day = map.get(key);
      day.events.push(ev);
      if (!day.byCat[ev.cat]) day.byCat[ev.cat] = [];
      day.byCat[ev.cat].push(ev);
    }
    // Sort: newest day first
    return Array.from(map.values()).sort((a, b) => (a.date < b.date ? 1 : -1));
  }, [allEvents]);

  if (days.length === 0) {
    return (
      <div style={S.card}>
        <SectionTitle icon="fa-calendar">Daily Timeline</SectionTitle>
        <EmptyState
          icon="fa-calendar-times-o"
          message={`No activity between ${range.startLabel} and ${range.endLabel}. Try a wider date range.`}
        />
      </div>
    );
  }

  return (
    <div style={S.colStack}>
      <div style={S.card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
          <SectionTitle icon="fa-calendar">
            Daily Timeline ({days.length} day{days.length > 1 ? 's' : ''})
          </SectionTitle>
          <div style={{ fontSize: 12, color: '#64748b' }}>
            <i className="fa fa-info-circle" /> Click a day or category to expand. Connections show how each day's events join across collections.
          </div>
        </div>
        <div style={S.dayTreeWrap}>
          {days.map((day, i) => (
            <DayNode key={day.date} day={day} defaultOpen={i === 0} />
          ))}
        </div>
      </div>
    </div>
  );
}

function DayNode({ day, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen);
  const cats = Object.keys(day.byCat);
  const totalAmount = day.events.reduce((a, e) => a + (e.amount || 0), 0);
  const dateObj = new Date(day.date + 'T00:00:00');
  const weekday = dateObj.toLocaleDateString('en-US', { weekday: 'long' });
  const long = dateObj.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

  return (
    <div style={S.dayCard}>
      <button onClick={() => setOpen(!open)} style={S.dayHead}>
        <span style={S.dayCalIcon}>
          <i className="fa fa-calendar-o" />
          <span style={S.dayCalDay}>{dateObj.getDate()}</span>
        </span>
        <div style={{ flex: 1, textAlign: 'left' }}>
          <div style={S.dayTitle}>{long}</div>
          <div style={S.daySub}>
            {weekday} · <strong>{day.events.length}</strong> events across <strong>{cats.length}</strong> {cats.length === 1 ? 'category' : 'categories'}
            {totalAmount > 0 && <> · ≈ {fmtMoney(totalAmount)}</>}
          </div>
        </div>
        <div style={S.dayChips}>
          {cats.slice(0, 6).map((c) => (
            <span
              key={c}
              style={{
                ...S.dayChip,
                background: CATEGORIES[c]?.color || '#64748b',
              }}
              title={CATEGORIES[c]?.label}
            >
              <i className={`fa ${CATEGORIES[c]?.icon || 'fa-circle'}`} /> {day.byCat[c].length}
            </span>
          ))}
          {cats.length > 6 && (
            <span style={{ ...S.dayChip, background: '#94a3b8' }}>+{cats.length - 6}</span>
          )}
        </div>
        <i
          className={`fa fa-chevron-${open ? 'down' : 'right'}`}
          style={{ color: '#64748b', marginLeft: 8 }}
        />
      </button>
      {open && (
        <div style={S.dayBody}>
          {cats.map((c) => (
            <CategoryNode key={c} catKey={c} events={day.byCat[c]} />
          ))}
        </div>
      )}
    </div>
  );
}

function CategoryNode({ catKey, events }) {
  const [open, setOpen] = useState(false);
  const cat = CATEGORIES[catKey] || { label: catKey, icon: 'fa-circle', color: '#64748b' };
  const totalAmount = events.reduce((a, e) => a + (e.amount || 0), 0);

  return (
    <div style={{ ...S.catCard, borderLeft: `4px solid ${cat.color}` }}>
      <button onClick={() => setOpen(!open)} style={S.catHead}>
        <i
          className={`fa fa-chevron-${open ? 'down' : 'right'}`}
          style={{ color: cat.color, fontSize: 11, width: 12 }}
        />
        <span style={{ ...S.catBadge, background: cat.color }}>
          <i className={`fa ${cat.icon}`} />
        </span>
        <span style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>{cat.label}</span>
        <span style={S.catCount}>{events.length}</span>
        {totalAmount > 0 && (
          <span style={{ marginLeft: 'auto', fontSize: 12, fontWeight: 700, color: cat.color }}>
            {fmtMoney(totalAmount)}
          </span>
        )}
      </button>
      {open && (
        <div style={S.eventList}>
          {events.map((e, i) => (
            <div key={i} style={S.eventRow}>
              <span style={{ ...S.eventDot, background: cat.color }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={S.eventTitle}>{e.title}</div>
                <div style={S.eventSub}>{e.sub}</div>
              </div>
              <div style={S.eventTime}>
                {new Date(e.when).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ───────────────────────── tab views ───────────────────────── */

function OverviewTab({ user, summary, upline, downline, history }) {
  return (
    <div style={S.colStack}>
      <div style={S.card}>
        <SectionTitle icon="fa-id-badge">Quick Identity</SectionTitle>
        <div style={S.gridAuto}>
          <KV label="Full Name" value={user.name} />
          <KV label="Username" value={`@${user.username}`} />
          <KV label="Phone" value={user.phone} />
          <KV label="Email" value={user.email || '—'} />
          <KV label="Reference (Sponsor)" value={user.reference || '—'} />
          <KV label="Placement" value={user.placement ? `${user.placement} (${user.position})` : '—'} />
          <KV label="Designation" value={`#${user.designation || 0}`} />
          <KV label="Joined" value={fmtDate(user.createdAt)} />
        </div>
      </div>

      <div style={S.card}>
        <SectionTitle icon="fa-bolt">Activity At a Glance</SectionTitle>
        <div style={S.gridAuto}>
          <StatCard icon="fa-money-bill-wave" label="Withdrawals" value={summary.counts.withdrawals} color="#0ea5e9" />
          <StatCard icon="fa-exchange" label="Conversions" value={summary.counts.conversions} color="#8b5cf6" />
          <StatCard icon="fa-arrow-up" label="Transfers Out" value={summary.counts.pointsSent} color="#ef4444" />
          <StatCard icon="fa-arrow-down" label="Transfers In" value={summary.counts.pointsReceived} color="#22c55e" />
          <StatCard icon="fa-shopping-cart" label="Orders Placed" value={summary.counts.orders} color="#f43f5e" />
          <StatCard icon="fa-trophy" label="Promotions" value={summary.counts.promotions} color="#f59e0b" />
          <StatCard icon="fa-globe" label="Global Entries" value={summary.counts.globalHistory} color="#06b6d4" />
          <StatCard icon="fa-bell" label="Notifications" value={summary.counts.notifications} color="#64748b" />
        </div>
      </div>

      <div style={S.card}>
        <SectionTitle icon="fa-sitemap">Network Snapshot</SectionTitle>
        <div style={S.gridAuto}>
          <NodeMini title="Sponsor (Upline)" user={upline.sponsor} fallback={user.reference || '—'} />
          <NodeMini title="Placement (Upline)" user={upline.placement} fallback={user.placement || '—'} />
          <NodeMini title="Direct Referrals" stat={`${summary.directReferrals} users`} />
          <NodeMini title="Placement Children" stat={`L:${downline.placementChildren.left ? '1' : '0'} · R:${downline.placementChildren.right ? '1' : '0'}`} />
        </div>
      </div>

      <div style={S.card}>
        <SectionTitle icon="fa-clock-o">Latest Activity</SectionTitle>
        <RecentTimeline history={history} />
      </div>
    </div>
  );
}

function NodeMini({ title, user, stat, fallback }) {
  return (
    <div style={S.statCard}>
      <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.5px' }}>
        {title}
      </div>
      {user ? (
        <>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#0f172a', marginTop: 6 }}>
            {user.name}
          </div>
          <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>@{user.username}</div>
          <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 6 }}>
            #{user.designation || 0} · {fmtNum(user.point || 0)} pts
          </div>
        </>
      ) : (
        <div style={{ fontSize: 16, fontWeight: 700, color: '#94a3b8', marginTop: 6 }}>
          {stat || fallback}
        </div>
      )}
    </div>
  );
}

function RecentTimeline({ history }) {
  // Build a unified recent-activity feed
  const events = useMemo(() => {
    const all = [];
    (history.withdrawals || []).slice(0, 5).forEach((w) =>
      all.push({
        when: w.createdAt,
        icon: 'fa-money-bill-wave',
        color: '#0ea5e9',
        title: `Withdrew ${fmtMoney(w.amount)}`,
        sub: `via ${w.paymentmethod} → ${w.to} · ${w.status ? 'Paid' : 'Pending'}`,
      })
    );
    (history.conversions || []).slice(0, 5).forEach((c) =>
      all.push({
        when: c.createdAt,
        icon: 'fa-exchange',
        color: '#8b5cf6',
        title: `Converted ${fmtNum(c.amount)} ${c.from} → ${c.to}`,
        sub: `Net ${fmtNum(c.netAmount || c.amount)} · ${c.status}`,
      })
    );
    (history.orders || []).slice(0, 5).forEach((o) =>
      all.push({
        when: o.createdAt,
        icon: 'fa-shopping-cart',
        color: '#f43f5e',
        title: `Placed order ${fmtMoney(o.totalPrice)}`,
        sub: `${o.orderItems?.length || 0} items · ${o.isPaid ? 'Paid' : 'Unpaid'} · ${o.isDelivered ? 'Delivered' : 'Pending delivery'}`,
      })
    );
    (history.pointsSent || []).slice(0, 5).forEach((p) =>
      all.push({
        when: p.createdAt,
        icon: 'fa-arrow-up',
        color: '#ef4444',
        title: `Sent ${fmtNum(p.amount)} points`,
        sub: `→ @${p.receiverUsername}`,
      })
    );
    (history.pointsReceived || []).slice(0, 5).forEach((p) =>
      all.push({
        when: p.createdAt,
        icon: 'fa-arrow-down',
        color: '#22c55e',
        title: `Received ${fmtNum(p.amount)} points`,
        sub: `← @${p.senderUsername || 'system'}`,
      })
    );
    return all
      .filter((e) => e.when)
      .sort((a, b) => new Date(b.when) - new Date(a.when))
      .slice(0, 12);
  }, [history]);

  if (events.length === 0) return <EmptyState message="No recent activity" />;

  return (
    <div style={S.timeline}>
      {events.map((e, i) => (
        <div key={i} style={S.timelineItem}>
          <div style={{ ...S.timelineDot, background: e.color }}>
            <i className={`fa ${e.icon}`} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, color: '#0f172a', fontWeight: 600 }}>{e.title}</div>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{e.sub}</div>
          </div>
          <div style={{ fontSize: 11, color: '#94a3b8', whiteSpace: 'nowrap', marginLeft: 12 }}>
            {fmtDateTime(e.when)}
          </div>
        </div>
      ))}
    </div>
  );
}

function ProfileTab({ user, tree }) {
  const groups = [
    {
      title: 'Personal',
      icon: 'fa-user',
      fields: [
        ['Full Name', user.name],
        ['Username', user.username],
        ['Phone', user.phone],
        ['Email', user.email || '—'],
        ['Gender', user.gender || '—'],
        ['Date of Birth', user.dob || '—'],
        ['Address', user.address || '—'],
        ['NID', user.nid || '—'],
        ["Father's Phone", user.fatherphone || '—'],
        ["Mother's Phone", user.motherphone || '—'],
        ["Spouse's Phone", user.spousephone || '—'],
        ['Facebook', user.fblink || '—'],
      ],
    },
    {
      title: 'Account',
      icon: 'fa-id-card',
      fields: [
        ['User ID', user._id],
        ['Reference (Sponsor)', user.reference || '—'],
        ['Placement', user.placement || '—'],
        ['Position', user.position || '—'],
        ['Designation', `#${user.designation || 0}`],
        ['Created By', user.createdBy || '—'],
        ['Joined', fmtDateTime(user.createdAt)],
        ['Updated', fmtDateTime(user.updatedAt)],
        ['Total IDs Created', user.totalCreatedId ?? 0],
        ['Active ID', user.isActiveId ? 'Yes' : 'No'],
        ['Status', user.status ? 'Active' : 'Disabled'],
        ['Blocked', user.isBlocked ? 'Yes' : 'No'],
      ],
    },
    {
      title: 'Banking',
      icon: 'fa-university',
      fields: [
        ['Bank Name', user.bankname || '—'],
        ['Bank Account', user.bankaccount || '—'],
        ['Bank Branch', user.bankbranch || '—'],
        ['Mobile Bank', user.mobilebankname || '—'],
        ['Mobile Bank Acc.', user.mobilebankaccount || '—'],
        ['Beneficiary Name', user.benificiaryname || '—'],
        ['Beneficiary NID', user.benificiarynid || '—'],
        ['Beneficiary Relation', user.benificiaryrelation || '—'],
      ],
    },
    {
      title: 'Balances & Tree',
      icon: 'fa-coins',
      fields: [
        ['Points', fmtNum(user.point)],
        ['Available Balance', fmtMoney(user.availablebalance)],
        ['Shopping Balance', fmtMoney(user.shoppingbalance)],
        ['Left Team Count', fmtNum(user.leftuser)],
        ['Right Team Count', fmtNum(user.rightuser)],
        ['Tree last update', tree ? fmtDateTime(tree.lastupdate) : '—'],
        ['Tree status', tree ? (tree.status ? 'Active' : 'Inactive') : '—'],
      ],
    },
  ];

  return (
    <div style={S.colStack}>
      {groups.map((g, i) => (
        <Collapsible key={g.title} icon={g.icon} title={g.title} defaultOpen={i === 0}>
          <div style={S.gridAuto}>
            {g.fields.map(([k, v]) => (
              <KV key={k} label={k} value={v} />
            ))}
          </div>
        </Collapsible>
      ))}
    </div>
  );
}

function KV({ label, value }) {
  return (
    <div style={S.kv}>
      <div style={S.kvLabel}>{label}</div>
      <div style={S.kvValue}>{value !== undefined && value !== null ? value : '—'}</div>
    </div>
  );
}

function FinancialTab({ history }) {
  return (
    <div style={S.colStack}>
      <Collapsible icon="fa-money-bill-wave" title="Withdrawal History" count={history.withdrawals.length} defaultOpen>
        {history.withdrawals.length === 0 ? (
          <EmptyState message="No withdrawals in this range" />
        ) : (
          <DataTable
            columns={[
              ['Date', (r) => fmtDateTime(r.createdAt)],
              ['Method', (r) => r.paymentmethod],
              ['Source', (r) => r.withdrawsource || '—'],
              ['To', (r) => r.to],
              ['Amount', (r) => fmtMoney(r.amount), 'right'],
              ['Charge', (r) => fmtMoney(r.charge), 'right'],
              ['Status', (r) => <StatusPill status={r.status ? 'Paid' : 'Pending'} />],
              ['Paid At', (r) => fmtDateTime(r.paidAt)],
              ['Paid By', (r) => r.paidBy || '—'],
            ]}
            rows={history.withdrawals}
          />
        )}
      </Collapsible>

      <Collapsible icon="fa-exchange" title="Conversion History" count={history.conversions.length} defaultOpen>
        {history.conversions.length === 0 ? (
          <EmptyState message="No conversions in this range" />
        ) : (
          <DataTable
            columns={[
              ['Date', (r) => fmtDateTime(r.createdAt)],
              ['From', (r) => <Pill text={r.from} color="#fbbf24" />],
              ['To', (r) => <Pill text={r.to} color="#34d399" />],
              ['Amount', (r) => fmtNum(r.amount), 'right'],
              ['Fee', (r) => fmtNum(r.feeApplied), 'right'],
              ['Net', (r) => fmtNum(r.netAmount), 'right'],
              ['Before', (r) => fmtNum(r.balanceBefore), 'right'],
              ['After', (r) => fmtNum(r.balanceAfter), 'right'],
              ['Status', (r) => <StatusPill status={r.status} />],
            ]}
            rows={history.conversions}
          />
        )}
      </Collapsible>

      <Collapsible icon="fa-receipt" title="Cost / Commission Splits" count={history.cost.length} defaultOpen={false}>
        {history.cost.length === 0 ? (
          <EmptyState message="No cost records" />
        ) : (
          <DataTable
            columns={[
              ['Date', (r) => fmtDateTime(r.createdAt)],
              ['Reference', (r) => fmtNum(r.reference), 'right'],
              ['Matching', (r) => fmtNum(r.matching), 'right'],
              ['Global', (r) => fmtNum(r.global), 'right'],
              ['Reward', (r) => fmtNum(r.reward), 'right'],
              ['Total', (r) => fmtNum(r.total), 'right'],
              ['Active', (r) => <StatusPill status={r.status ? 'true' : 'false'} />],
            ]}
            rows={history.cost}
          />
        )}
      </Collapsible>
    </div>
  );
}

function TransfersTab({ history, username }) {
  return (
    <div style={S.colStack}>
      <Collapsible icon="fa-arrow-up" title="Points Sent (Outgoing)" count={history.pointsSent.length} defaultOpen>
        {history.pointsSent.length === 0 ? (
          <EmptyState message="No outgoing transfers in this range" />
        ) : (
          <DataTable
            columns={[
              ['Date', (r) => fmtDateTime(r.createdAt)],
              ['From', () => `@${username}`],
              ['To', (r) => <strong style={{ color: '#0ea5e9' }}>@{r.receiverUsername}</strong>],
              ['Amount', (r) => fmtNum(r.amount), 'right'],
              ['Charge', (r) => fmtNum(r.transferCharge), 'right'],
              ['Sender Bal.', (r) => fmtNum(r.senderBalance), 'right'],
              ['Status', (r) => <StatusPill status={r.isPaid ? 'Paid' : 'Pending'} />],
            ]}
            rows={history.pointsSent}
          />
        )}
      </Collapsible>

      <Collapsible icon="fa-arrow-down" title="Points Received (Incoming)" count={history.pointsReceived.length} defaultOpen>
        {history.pointsReceived.length === 0 ? (
          <EmptyState message="No incoming transfers in this range" />
        ) : (
          <DataTable
            columns={[
              ['Date', (r) => fmtDateTime(r.createdAt)],
              ['From', (r) => <strong style={{ color: '#16a34a' }}>@{r.senderUsername || 'system'}</strong>],
              ['To', () => `@${username}`],
              ['Amount', (r) => fmtNum(r.amount), 'right'],
              ['Note', (r) => r.sendInfo || '—'],
              ['Status', (r) => <StatusPill status={r.isPaid ? 'Paid' : 'Pending'} />],
            ]}
            rows={history.pointsReceived}
          />
        )}
      </Collapsible>
    </div>
  );
}

function EarningsTab({ history }) {
  const sections = [
    { key: 'availableBalance', title: 'Available Balance Ledger', icon: 'fa-wallet', data: history.availableBalance },
    { key: 'matchingPoints', title: 'Matching Points', icon: 'fa-handshake-o', data: history.matchingPoints },
    { key: 'referencePoints', title: 'Reference Points', icon: 'fa-link', data: history.referencePoints },
    { key: 'shoppingPoints', title: 'Shopping Points', icon: 'fa-shopping-bag', data: history.shoppingPoints },
    { key: 'incomeGenerations', title: 'Income Generation', icon: 'fa-bolt', data: history.incomeGenerations, withGen: true },
  ];

  return (
    <div style={S.colStack}>
      {sections.map((s, i) => (
        <Collapsible key={s.key} icon={s.icon} title={s.title} count={s.data.length} defaultOpen={i === 0}>
          {s.data.length === 0 ? (
            <EmptyState message={`No ${s.title.toLowerCase()} records in this range`} />
          ) : (
            <DataTable
              columns={[
                ['Date', (r) => fmtDateTime(r.createdAt)],
                ['Amount', (r) => fmtMoney(r.amount), 'right'],
                ...(s.withGen ? [['Gen', (r) => `Gen #${r.generation || '—'}`]] : []),
                ['Status', (r) => <StatusPill status={r.status} />],
                ['Details', (r) => r.details || '—'],
              ]}
              rows={s.data}
            />
          )}
        </Collapsible>
      ))}
    </div>
  );
}

function OrdersTab({ history }) {
  const [open, setOpen] = useState(null);
  return (
    <Collapsible icon="fa-shopping-cart" title="Order History" count={history.orders.length} defaultOpen>
      {history.orders.length === 0 ? (
        <EmptyState message="No orders placed in this range" />
      ) : (
        <div style={S.tableWrap}>
          <table style={S.table}>
            <thead>
              <tr>
                <th style={S.th}></th>
                <th style={S.th}>Date</th>
                <th style={S.th}>Items</th>
                <th style={{ ...S.th, textAlign: 'right' }}>Total</th>
                <th style={{ ...S.th, textAlign: 'right' }}>Points</th>
                <th style={S.th}>Payment</th>
                <th style={S.th}>Delivery</th>
                <th style={S.th}>Action</th>
              </tr>
            </thead>
            <tbody>
              {history.orders.map((o) => (
                <React.Fragment key={o._id}>
                  <tr style={S.tr}>
                    <td style={S.td}>
                      <button
                        onClick={() => setOpen(open === o._id ? null : o._id)}
                        style={S.expandBtn}
                      >
                        <i className={`fa fa-chevron-${open === o._id ? 'down' : 'right'}`} />
                      </button>
                    </td>
                    <td style={S.td}>{fmtDateTime(o.createdAt)}</td>
                    <td style={S.td}>{o.orderItems?.length || 0} items</td>
                    <td style={{ ...S.td, textAlign: 'right', fontWeight: 700 }}>
                      {fmtMoney(o.totalPrice)}
                    </td>
                    <td style={{ ...S.td, textAlign: 'right' }}>{fmtNum(o.totalPoint)}</td>
                    <td style={S.td}>
                      <StatusPill status={o.isPaid ? 'Paid' : 'Pending'} />
                      {o.isPaid && (
                        <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>
                          {fmtDate(o.paidAt)}
                        </div>
                      )}
                    </td>
                    <td style={S.td}>
                      <StatusPill status={o.isDelivered ? 'Approved' : 'Pending'} />
                      {o.isDelivered && (
                        <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>
                          {fmtDate(o.deliveredAt)}
                        </div>
                      )}
                    </td>
                    <td style={S.td}>
                      <Link to={`/order/${o._id}`} style={S.linkBtn}>
                        <i className="fa fa-external-link" /> Open
                      </Link>
                    </td>
                  </tr>
                  {open === o._id && (
                    <tr>
                      <td colSpan={8} style={{ background: '#f8fafc', padding: 16 }}>
                        <div style={{ display: 'grid', gap: 8 }}>
                          <strong style={{ color: '#0f172a', fontSize: 13 }}>
                            <i className="fa fa-truck" /> {o.shippingAddress?.fullName} — {o.shippingAddress?.streetAddress}, {o.shippingAddress?.city}
                          </strong>
                          <div style={{ fontSize: 12, color: '#64748b' }}>
                            Method: {o.shippingAddress?.delivery_method} · Phone: {o.shippingAddress?.phone || '—'} · Pay: {o.paymentMethod}
                          </div>
                          <div style={{ marginTop: 4, display: 'grid', gap: 6 }}>
                            {(o.orderItems || []).map((it, idx) => (
                              <div
                                key={idx}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 12,
                                  background: '#fff',
                                  padding: 8,
                                  borderRadius: 8,
                                  border: '1px solid #e2e8f0',
                                }}
                              >
                                <img
                                  src={it.image}
                                  alt=""
                                  style={{ width: 40, height: 40, borderRadius: 6, objectFit: 'cover' }}
                                />
                                <div style={{ flex: 1 }}>
                                  <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>{it.name}</div>
                                  <div style={{ fontSize: 11, color: '#64748b' }}>x {it.quantity}</div>
                                </div>
                                <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>
                                  {fmtMoney(it.price * it.quantity)}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Collapsible>
  );
}

function NetworkTab({ user, upline, downline }) {
  const left = downline.placementChildren.left;
  const right = downline.placementChildren.right;

  return (
    <div style={S.colStack}>
      <Collapsible
        icon="fa-share-alt"
        title="Sponsor Tree (by Reference)"
        count={downline.referralsCount}
        defaultOpen
      >
        <div style={S.treeWrap}>
          <div style={S.treeRow}>
            <TreeNode user={upline.sponsor} label="Sponsor" emptyText={user.reference || 'No sponsor'} />
          </div>
          <div style={S.treeConn} />
          <div style={S.treeRow}>
            <TreeNode user={user} label="This User" highlight />
          </div>
          <div style={S.treeConn} />
          <div style={{ fontSize: 13, color: '#64748b', marginBottom: 12 }}>
            Direct Referrals — <strong>{downline.referralsCount}</strong> users{downline.referrals.length < downline.referralsCount ? ` (showing newest ${downline.referrals.length})` : ''}
          </div>
          {downline.referrals.length === 0 ? (
            <EmptyState message="This user hasn't referred anyone yet" />
          ) : (
            <div style={S.referralGrid}>
              {downline.referrals.map((r) => (
                <ReferralCard key={r._id} u={r} />
              ))}
            </div>
          )}
        </div>
      </Collapsible>

      <Collapsible icon="fa-sitemap" title="Placement Tree (Binary)" defaultOpen>
        <div style={S.treeWrap}>
          <div style={S.treeRow}>
            <TreeNode user={upline.placement} label="Placement Parent" emptyText={user.placement || 'No parent'} />
          </div>
          <div style={S.treeConn} />
          <div style={S.treeRow}>
            <TreeNode user={user} label={`This User · ${user.position?.toUpperCase() || ''}`} highlight />
          </div>
          <div style={S.binaryConn}>
            <div style={S.binaryLineL} />
            <div style={S.binaryLineR} />
          </div>
          <div style={S.binaryRow}>
            <TreeNode user={left} label="LEFT child" sideColor="#ef4444" emptyText="No left child" />
            <TreeNode user={right} label="RIGHT child" sideColor="#0ea5e9" emptyText="No right child" />
          </div>
          <div style={{ marginTop: 18, display: 'flex', gap: 12, justifyContent: 'center' }}>
            <StatCard icon="fa-arrow-circle-left" label="Left team count" value={fmtNum(user.leftuser)} color="#ef4444" />
            <StatCard icon="fa-arrow-circle-right" label="Right team count" value={fmtNum(user.rightuser)} color="#0ea5e9" />
          </div>
        </div>
      </Collapsible>
    </div>
  );
}

function TreeNode({ user, label, highlight, sideColor, emptyText }) {
  if (!user) {
    return (
      <div style={{ ...S.treeNode, background: '#f1f5f9', borderColor: '#e2e8f0', color: '#94a3b8' }}>
        <div style={S.treeLabel}>{label}</div>
        <div style={{ fontSize: 13, fontWeight: 600 }}>{emptyText}</div>
      </div>
    );
  }
  return (
    <Link
      to={`/admin/userreport/${user._id}`}
      style={{
        ...S.treeNode,
        ...(highlight ? S.treeNodeHi : {}),
        ...(sideColor ? { borderColor: sideColor } : {}),
      }}
    >
      <div style={S.treeLabel}>{label}</div>
      <div style={S.treeAvatar}>{initials(user.name)}</div>
      <div style={{ fontWeight: 700, fontSize: 14, color: highlight ? '#fff' : '#0f172a' }}>{user.name}</div>
      <div style={{ fontSize: 12, color: highlight ? 'rgba(255,255,255,.85)' : '#64748b' }}>@{user.username}</div>
      <div
        style={{
          marginTop: 8,
          display: 'flex',
          gap: 6,
          flexWrap: 'wrap',
          justifyContent: 'center',
        }}
      >
        <span style={S.treeStat}>#{user.designation || 0}</span>
        <span style={S.treeStat}>{fmtNum(user.point || 0)} pts</span>
        <span style={S.treeStat}>L:{user.leftuser || 0}/R:{user.rightuser || 0}</span>
      </div>
    </Link>
  );
}

function ReferralCard({ u }) {
  return (
    <Link to={`/admin/userreport/${u._id}`} style={S.refCard}>
      <div style={{ ...S.refAvatar, background: u.status ? 'linear-gradient(135deg,#6366f1,#8b5cf6)' : '#94a3b8' }}>
        {initials(u.name)}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {u.name}
        </div>
        <div style={{ fontSize: 11, color: '#64748b' }}>@{u.username}</div>
        <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>
          #{u.designation || 0} · {fmtNum(u.point || 0)} pts · L:{u.leftuser || 0}/R:{u.rightuser || 0}
        </div>
      </div>
      {!u.status && <span style={{ ...S.refMini, background: '#fee2e2', color: '#991b1b' }}>Disabled</span>}
      {u.isBlocked && <span style={{ ...S.refMini, background: '#fee2e2', color: '#991b1b' }}>Blocked</span>}
    </Link>
  );
}

function PromotionsTab({ history }) {
  return (
    <div style={S.colStack}>
      <Collapsible icon="fa-trophy" title="Promotion / Position History" count={history.promotions.length} defaultOpen>
        {history.promotions.length === 0 ? (
          <EmptyState message="No promotion records in this range" />
        ) : (
          <DataTable
            columns={[
              ['Date', (r) => fmtDateTime(r.createdAt)],
              ['Position', (r) => `${r.position_name} (#${r.position_serial})`],
              ['Carry L/R', (r) => `${fmtNum(r.carry_left)} / ${fmtNum(r.carry_right)}`],
              ['Status', (r) => <StatusPill status={r.status} />],
              ['Reward', (r) => r.reward],
              ['Updated By', (r) => r.updated_by || '—'],
            ]}
            rows={history.promotions}
          />
        )}
      </Collapsible>

      <Collapsible icon="fa-globe" title="Global Pool History" count={history.globalHistory.length} defaultOpen>
        {history.globalHistory.length === 0 ? (
          <EmptyState message="No global pool entries in this range" />
        ) : (
          <DataTable
            columns={[
              ['Date', (r) => fmtDateTime(r.createdAt)],
              ['Designation', (r) => `#${r.designation}`],
              ['Amount', (r) => fmtMoney(r.amount), 'right'],
              ['Status', (r) => <StatusPill status={r.status} />],
            ]}
            rows={history.globalHistory}
          />
        )}
      </Collapsible>
    </div>
  );
}

function ActivityTab({ history }) {
  return (
    <div style={S.colStack}>
      <Collapsible icon="fa-bell" title="Notifications" count={history.notifications.length} defaultOpen>
        {history.notifications.length === 0 ? (
          <EmptyState message="No notifications in this range" />
        ) : (
          <DataTable
            columns={[
              ['Date', (r) => fmtDateTime(r.createdAt)],
              ['Message', (r) => r.details],
              ['Seen', (r) => <StatusPill status={r.seen ? 'Approved' : 'Pending'} />],
              ['Seen At', (r) => fmtDateTime(r.seenAt)],
            ]}
            rows={history.notifications}
          />
        )}
      </Collapsible>

      <Collapsible icon="fa-comments" title="Support Chats" count={history.chats.length} defaultOpen>
        {history.chats.length === 0 ? (
          <EmptyState message="No support chats in this range" />
        ) : (
          <div style={{ display: 'grid', gap: 10 }}>
            {history.chats.map((c) => (
              <div key={c._id} style={S.chatCard}>
                <div style={{ fontSize: 11, color: '#64748b', marginBottom: 4 }}>
                  <i className="fa fa-clock-o" /> {fmtDateTime(c.createdAt)}
                </div>
                <div style={{ fontSize: 14, color: '#0f172a' }}>
                  <strong>User:</strong> {c.message}
                </div>
                {c.reply && (
                  <div style={{ fontSize: 13, color: '#16a34a', marginTop: 4 }}>
                    <strong>Reply:</strong> {c.reply}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Collapsible>

      <Collapsible icon="fa-desktop" title="Active Sessions" count={history.sessions.length} defaultOpen={false}>
        {history.sessions.length === 0 ? (
          <EmptyState message="No session records" />
        ) : (
          <DataTable
            columns={[
              ['Login', (r) => fmtDateTime(r.loginAt)],
              ['Last Active', (r) => fmtDateTime(r.lastActive)],
              ['IP', (r) => r.ip || '—'],
              ['City', (r) => r.city || '—'],
              ['Country', (r) => r.country || '—'],
              ['Device', (r) => (r.userAgent ? r.userAgent.slice(0, 40) + '…' : '—')],
            ]}
            rows={history.sessions}
          />
        )}
      </Collapsible>
    </div>
  );
}

/* ────────────────── data-table helper ────────────────── */
function DataTable({ columns, rows }) {
  return (
    <div style={S.tableWrap}>
      <table style={S.table}>
        <thead>
          <tr>
            {columns.map(([h, , align], i) => (
              <th key={i} style={{ ...S.th, textAlign: align || 'left' }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r._id || i} style={S.tr}>
              {columns.map(([, fn, align], ci) => (
                <td key={ci} style={{ ...S.td, textAlign: align || 'left' }}>
                  {fn(r)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Pill({ text, color }) {
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '3px 9px',
        borderRadius: 99,
        fontSize: 11,
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '.4px',
        color: '#fff',
        background: color,
      }}
    >
      {text}
    </span>
  );
}

/* ────────────────── styles ────────────────── */

const S = {
  shell: {
    padding: '24px',
    maxWidth: 1500,
    margin: '0 auto',
    background: '#f8fafc',
    minHeight: '100vh',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  centerBox: { textAlign: 'center', padding: '120px 20px' },
  spinner: {
    width: 56,
    height: 56,
    border: '5px solid #e2e8f0',
    borderTopColor: '#6366f1',
    borderRadius: '50%',
    animation: 'reportspin 0.8s linear infinite',
    margin: '0 auto',
  },
  errorBox: {
    background: '#fef2f2',
    border: '1px solid #fecaca',
    padding: 32,
    borderRadius: 16,
    textAlign: 'center',
    maxWidth: 500,
    margin: '60px auto',
  },
  btnGhost: {
    marginTop: 16,
    padding: '10px 20px',
    borderRadius: 10,
    border: '1px solid #cbd5e1',
    background: '#fff',
    color: '#475569',
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 600,
  },

  // Hero
  hero: {
    position: 'relative',
    background: 'linear-gradient(135deg,#1e293b 0%,#312e81 50%,#7c3aed 100%)',
    borderRadius: 20,
    padding: '32px 28px',
    color: '#fff',
    marginBottom: 24,
    overflow: 'hidden',
    boxShadow: '0 10px 30px rgba(76,29,149,0.25)',
  },
  heroPattern: {
    position: 'absolute',
    inset: 0,
    backgroundImage:
      'radial-gradient(circle at 20% 20%, rgba(255,255,255,.1) 0%, transparent 40%), radial-gradient(circle at 80% 80%, rgba(255,255,255,.08) 0%, transparent 40%)',
    pointerEvents: 'none',
  },
  backBtn: {
    position: 'relative',
    zIndex: 1,
    background: 'rgba(255,255,255,.15)',
    border: '1px solid rgba(255,255,255,.25)',
    color: '#fff',
    padding: '8px 16px',
    borderRadius: 99,
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
    marginBottom: 18,
  },
  heroBody: {
    position: 'relative',
    zIndex: 1,
    display: 'flex',
    gap: 22,
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  heroAvatar: {
    width: 96,
    height: 96,
    borderRadius: '50%',
    background: 'linear-gradient(135deg,#fbbf24,#f97316)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 36,
    fontWeight: 800,
    color: '#fff',
    flexShrink: 0,
    border: '4px solid rgba(255,255,255,0.2)',
    boxShadow: '0 8px 20px rgba(0,0,0,.25)',
  },
  heroInfo: { flex: 1, minWidth: 240 },
  heroName: { margin: 0, fontSize: 32, fontWeight: 800, letterSpacing: '-.5px' },
  heroMeta: {
    marginTop: 8,
    display: 'flex',
    flexWrap: 'wrap',
    gap: 10,
  },
  heroChip: {
    fontSize: 12,
    fontWeight: 500,
    padding: '4px 12px',
    borderRadius: 99,
    background: 'rgba(255,255,255,.15)',
    border: '1px solid rgba(255,255,255,.2)',
  },
  heroBadges: { marginTop: 14, display: 'flex', flexWrap: 'wrap', gap: 8 },
  heroActions: { display: 'flex', flexDirection: 'column', gap: 8 },
  btnLight: {
    padding: '10px 18px',
    borderRadius: 10,
    background: 'rgba(255,255,255,.95)',
    color: '#1e293b',
    fontSize: 13,
    fontWeight: 700,
    border: 'none',
    cursor: 'pointer',
    textDecoration: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    whiteSpace: 'nowrap',
  },

  // Summary cards
  summaryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: 16,
    marginBottom: 20,
  },
  sumCard: {
    padding: '20px 22px',
    borderRadius: 16,
    color: '#fff',
    boxShadow: '0 4px 14px rgba(15,23,42,.08)',
    position: 'relative',
    overflow: 'hidden',
  },
  sumIcon: {
    position: 'absolute',
    top: 14,
    right: 14,
    width: 36,
    height: 36,
    borderRadius: 10,
    background: 'rgba(255,255,255,.18)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 16,
  },
  sumValue: { fontSize: 26, fontWeight: 800, letterSpacing: '-.5px', marginBottom: 4 },
  sumLabel: { fontSize: 13, fontWeight: 600, opacity: 0.95, marginBottom: 4 },
  sumSub: { fontSize: 11, opacity: 0.85 },

  // Stat card
  statCard: {
    background: '#fff',
    borderRadius: 12,
    padding: '14px 16px',
    border: '1px solid #e2e8f0',
    minHeight: 70,
  },
  statValue: { fontSize: 22, fontWeight: 800, color: '#0f172a' },
  statLabel: { fontSize: 11, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.4px', marginTop: 4 },

  // Section
  card: {
    background: '#fff',
    borderRadius: 16,
    padding: 22,
    border: '1px solid #e2e8f0',
    boxShadow: '0 1px 3px rgba(15,23,42,.04)',
  },
  colStack: { display: 'grid', gap: 16 },
  sectionTitle: {
    margin: '0 0 16px',
    fontSize: 16,
    fontWeight: 700,
    color: '#0f172a',
    display: 'flex',
    alignItems: 'center',
  },
  sectionCount: {
    marginLeft: 8,
    background: '#eef2ff',
    color: '#4f46e5',
    padding: '2px 10px',
    borderRadius: 99,
    fontSize: 11,
    fontWeight: 700,
  },
  gridAuto: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
    gap: 12,
  },

  // KV
  kv: {
    padding: '10px 12px',
    background: '#f8fafc',
    borderRadius: 8,
    border: '1px solid #f1f5f9',
  },
  kvLabel: { fontSize: 11, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.4px', marginBottom: 4 },
  kvValue: { fontSize: 13, color: '#0f172a', fontWeight: 600, wordBreak: 'break-word' },

  // Tabs
  tabBar: {
    display: 'flex',
    gap: 6,
    marginBottom: 20,
    background: '#fff',
    padding: 6,
    borderRadius: 14,
    border: '1px solid #e2e8f0',
    overflowX: 'auto',
    boxShadow: '0 1px 3px rgba(15,23,42,.04)',
  },
  tabBtn: {
    padding: '10px 16px',
    border: 'none',
    background: 'transparent',
    color: '#475569',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    borderRadius: 10,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    whiteSpace: 'nowrap',
    transition: 'all .15s',
  },
  tabBtnActive: {
    background: 'linear-gradient(135deg,#6366f1,#8b5cf6)',
    color: '#fff',
    boxShadow: '0 4px 10px rgba(99,102,241,.3)',
  },
  tabCount: {
    background: '#e2e8f0',
    color: '#475569',
    padding: '2px 8px',
    borderRadius: 99,
    fontSize: 11,
    fontWeight: 700,
    marginLeft: 2,
  },
  tabCountActive: { background: 'rgba(255,255,255,.25)', color: '#fff' },
  tabPanel: { marginBottom: 24 },

  // Bar chart
  barChart: {
    display: 'flex',
    height: 14,
    borderRadius: 99,
    overflow: 'hidden',
    background: '#f1f5f9',
    marginBottom: 16,
  },
  legendRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
    gap: 12,
  },
  legendItem: { display: 'flex', alignItems: 'center', gap: 8 },
  legendDot: { width: 12, height: 12, borderRadius: 4, flexShrink: 0 },

  // Table
  tableWrap: { overflowX: 'auto', WebkitOverflowScrolling: 'touch' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: {
    padding: '10px 12px',
    fontSize: 11,
    fontWeight: 700,
    color: '#64748b',
    textTransform: 'uppercase',
    letterSpacing: '.5px',
    background: '#f8fafc',
    borderBottom: '2px solid #e2e8f0',
    whiteSpace: 'nowrap',
  },
  td: {
    padding: '10px 12px',
    borderBottom: '1px solid #f1f5f9',
    color: '#1e293b',
    fontSize: 13,
    verticalAlign: 'top',
  },
  tr: { transition: 'background .15s' },

  // Empty
  empty: {
    textAlign: 'center',
    padding: '40px 20px',
    background: '#f8fafc',
    borderRadius: 12,
    border: '1px dashed #e2e8f0',
  },

  // Timeline
  timeline: { display: 'flex', flexDirection: 'column', gap: 12 },
  timelineItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 12,
    padding: '12px 14px',
    background: '#f8fafc',
    borderRadius: 12,
    border: '1px solid #f1f5f9',
  },
  timelineDot: {
    width: 32,
    height: 32,
    borderRadius: 10,
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 14,
    flexShrink: 0,
  },

  // Tree
  treeWrap: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '14px 0',
  },
  treeRow: { display: 'flex', justifyContent: 'center', gap: 24 },
  treeConn: { width: 2, height: 28, background: '#cbd5e1' },
  binaryConn: { display: 'flex', justifyContent: 'space-between', width: 360, height: 30, position: 'relative', maxWidth: '90%' },
  binaryLineL: {
    width: '50%',
    height: 30,
    borderLeft: '2px solid #cbd5e1',
    borderTop: '2px solid #cbd5e1',
    borderTopLeftRadius: 12,
  },
  binaryLineR: {
    width: '50%',
    height: 30,
    borderRight: '2px solid #cbd5e1',
    borderTop: '2px solid #cbd5e1',
    borderTopRightRadius: 12,
  },
  binaryRow: { display: 'flex', justifyContent: 'space-between', gap: 24, width: 480, maxWidth: '95%' },
  treeNode: {
    background: '#fff',
    border: '2px solid #e2e8f0',
    borderRadius: 14,
    padding: '14px 18px',
    minWidth: 180,
    textAlign: 'center',
    textDecoration: 'none',
    display: 'block',
    transition: 'transform .15s',
  },
  treeNodeHi: {
    background: 'linear-gradient(135deg,#6366f1,#8b5cf6)',
    borderColor: '#6366f1',
    color: '#fff',
    boxShadow: '0 6px 16px rgba(99,102,241,.3)',
  },
  treeAvatar: {
    width: 44,
    height: 44,
    borderRadius: '50%',
    background: 'linear-gradient(135deg,#06b6d4,#3b82f6)',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 800,
    fontSize: 16,
    margin: '6px auto',
  },
  treeLabel: {
    fontSize: 10,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '.5px',
    opacity: 0.7,
    marginBottom: 4,
  },
  treeStat: {
    fontSize: 10,
    fontWeight: 600,
    padding: '2px 8px',
    borderRadius: 99,
    background: 'rgba(15,23,42,.08)',
    color: 'inherit',
  },
  referralGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
    gap: 10,
    marginTop: 8,
  },
  refCard: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: 12,
    background: '#f8fafc',
    border: '1px solid #e2e8f0',
    borderRadius: 12,
    textDecoration: 'none',
  },
  refAvatar: {
    width: 44,
    height: 44,
    borderRadius: '50%',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 14,
    fontWeight: 800,
    flexShrink: 0,
  },
  refMini: { fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 99 },

  expandBtn: {
    background: '#eef2ff',
    border: 'none',
    width: 28,
    height: 28,
    borderRadius: 8,
    color: '#4f46e5',
    cursor: 'pointer',
    fontSize: 12,
  },
  linkBtn: {
    padding: '6px 12px',
    borderRadius: 8,
    background: '#eef2ff',
    color: '#4338ca',
    fontSize: 12,
    fontWeight: 600,
    textDecoration: 'none',
  },

  // Chat
  chatCard: {
    background: '#f8fafc',
    border: '1px solid #e2e8f0',
    borderRadius: 12,
    padding: '12px 14px',
  },

  footer: {
    textAlign: 'center',
    fontSize: 12,
    color: '#94a3b8',
    padding: '20px 0 40px',
  },

  // Date filter bar
  filterBar: {
    background: '#fff',
    borderRadius: 16,
    padding: 18,
    border: '1px solid #e2e8f0',
    boxShadow: '0 1px 3px rgba(15,23,42,.04)',
    marginBottom: 16,
  },
  filterHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
    flexWrap: 'wrap',
    gap: 8,
  },
  filterTitle: {
    fontSize: 15,
    fontWeight: 700,
    color: '#0f172a',
  },
  filterSub: {
    fontSize: 12,
    color: '#64748b',
    marginTop: 2,
  },
  presetRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 6,
    marginBottom: 12,
  },
  presetBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '7px 14px',
    border: '1px solid #e2e8f0',
    background: '#f8fafc',
    color: '#475569',
    fontSize: 12,
    fontWeight: 600,
    borderRadius: 99,
    cursor: 'pointer',
    transition: 'all .15s',
  },
  presetBtnActive: {
    background: 'linear-gradient(135deg,#6366f1,#8b5cf6)',
    color: '#fff',
    borderColor: 'transparent',
    boxShadow: '0 3px 8px rgba(99,102,241,.25)',
  },
  customRange: {
    display: 'flex',
    gap: 12,
    alignItems: 'flex-end',
    flexWrap: 'wrap',
    paddingTop: 12,
    borderTop: '1px dashed #e2e8f0',
  },
  dateGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  dateLabel: {
    fontSize: 10,
    fontWeight: 700,
    color: '#64748b',
    textTransform: 'uppercase',
    letterSpacing: '.5px',
  },
  dateInput: {
    border: '1px solid #cbd5e1',
    borderRadius: 8,
    padding: '8px 12px',
    fontSize: 13,
    color: '#0f172a',
    outline: 'none',
    fontFamily: 'inherit',
  },

  // KPI strip (filtered)
  kpiStrip: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: 10,
    marginBottom: 18,
  },
  kpiCell: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    background: '#fff',
    padding: '12px 14px',
    borderRadius: 12,
    border: '1px solid #e2e8f0',
    boxShadow: '0 1px 2px rgba(15,23,42,.03)',
  },
  kpiIcon: {
    width: 34,
    height: 34,
    borderRadius: 10,
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 14,
    flexShrink: 0,
  },
  kpiTotal: {
    fontSize: 16,
    fontWeight: 800,
    color: '#0f172a',
    lineHeight: 1.1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  kpiLabel: {
    fontSize: 11,
    color: '#64748b',
    fontWeight: 600,
    marginTop: 3,
  },

  // Collapsible
  collapseHead: {
    width: '100%',
    background: 'transparent',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    textAlign: 'left',
  },
  collapseHeadL: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flex: 1,
    minWidth: 0,
  },

  // Daily timeline tree
  dayTreeWrap: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    marginTop: 4,
  },
  dayCard: {
    background: '#f8fafc',
    border: '1px solid #e2e8f0',
    borderRadius: 14,
    overflow: 'hidden',
  },
  dayHead: {
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    padding: '14px 18px',
    width: '100%',
    background: '#fff',
    border: 'none',
    cursor: 'pointer',
    textAlign: 'left',
    borderBottom: '1px solid #e2e8f0',
  },
  dayCalIcon: {
    position: 'relative',
    width: 44,
    height: 44,
    background: 'linear-gradient(135deg,#6366f1,#8b5cf6)',
    color: '#fff',
    borderRadius: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 18,
    flexShrink: 0,
  },
  dayCalDay: {
    position: 'absolute',
    bottom: 4,
    right: 4,
    background: '#fff',
    color: '#6366f1',
    fontSize: 9,
    fontWeight: 800,
    padding: '1px 4px',
    borderRadius: 4,
    lineHeight: 1,
  },
  dayTitle: {
    fontSize: 15,
    fontWeight: 700,
    color: '#0f172a',
  },
  daySub: {
    fontSize: 12,
    color: '#64748b',
    marginTop: 2,
  },
  dayChips: {
    display: 'flex',
    gap: 4,
    flexWrap: 'wrap',
    maxWidth: 280,
    justifyContent: 'flex-end',
  },
  dayChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    padding: '3px 8px',
    color: '#fff',
    fontSize: 11,
    fontWeight: 700,
    borderRadius: 99,
    whiteSpace: 'nowrap',
  },
  dayBody: {
    padding: '10px 14px 14px 30px',
    display: 'grid',
    gap: 8,
    position: 'relative',
  },
  catCard: {
    background: '#fff',
    borderRadius: 10,
    border: '1px solid #e2e8f0',
    padding: 0,
    overflow: 'hidden',
  },
  catHead: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '10px 14px',
    width: '100%',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    textAlign: 'left',
  },
  catBadge: {
    width: 26,
    height: 26,
    borderRadius: 7,
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 12,
    flexShrink: 0,
  },
  catCount: {
    background: '#eef2ff',
    color: '#4f46e5',
    padding: '2px 9px',
    borderRadius: 99,
    fontSize: 11,
    fontWeight: 700,
  },
  eventList: {
    padding: '4px 14px 14px 50px',
    display: 'grid',
    gap: 8,
    borderTop: '1px dashed #e2e8f0',
  },
  eventRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
    padding: '8px 12px',
    background: '#f8fafc',
    borderRadius: 8,
  },
  eventDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    marginTop: 6,
    flexShrink: 0,
  },
  eventTitle: {
    fontSize: 13,
    color: '#0f172a',
    fontWeight: 600,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  eventSub: {
    fontSize: 11,
    color: '#64748b',
    marginTop: 2,
  },
  eventTime: {
    fontSize: 11,
    color: '#94a3b8',
    fontWeight: 600,
    whiteSpace: 'nowrap',
    marginLeft: 8,
  },
};

function SpinnerCSS() {
  return (
    <style>{`@keyframes reportspin { to { transform: rotate(360deg); } }`}</style>
  );
}

function ResponsiveCSS() {
  return (
    <style>{`
      @keyframes reportspin { to { transform: rotate(360deg); } }
      @media print {
        button { display: none !important; }
        .no-print { display: none !important; }
      }
      @media (max-width: 720px) {
        h1 { font-size: 22px !important; }
      }
    `}</style>
  );
}
